import { disableBodyScroll, enableBodyScroll } from './bodyScrollLock';
import { waitForTransition } from './transition';

export const MODAL_EVENTS = Object.freeze({
  SHOW: 'ui:modal:show',
  SHOWN: 'ui:modal:shown',
  HIDE: 'ui:modal:hide',
  HIDDEN: 'ui:modal:hidden',
});

const FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const instances = new WeakMap();
let activeModal = null;

function dispatch(element, name, relatedTarget) {
  return element.dispatchEvent(
    new CustomEvent(name, {
      bubbles: true,
      cancelable: name === MODAL_EVENTS.SHOW || name === MODAL_EVENTS.HIDE,
      detail: { relatedTarget },
    })
  );
}

function isVisible(element) {
  return (
    element.getClientRects().length > 0 &&
    element.getAttribute('aria-hidden') !== 'true' &&
    !element.hasAttribute('hidden')
  );
}

function focusableElements(element) {
  return [...element.querySelectorAll(FOCUSABLE_SELECTOR)].filter(isVisible);
}

function focusElement(element) {
  if (element && document.contains(element) && typeof element.focus === 'function') {
    element.focus({ preventScroll: true });
    return document.activeElement === element;
  }

  return false;
}

function readBooleanOption(value, fallback) {
  if (value === 'false' || value === false) {
    return false;
  }
  if (value === 'true' || value === true) {
    return true;
  }

  return fallback;
}

function resolveElement(elementOrSelector) {
  if (typeof elementOrSelector === 'string') {
    try {
      return document.querySelector(elementOrSelector);
    } catch {
      return null;
    }
  }

  return elementOrSelector;
}

export class Modal {
  constructor(element, options = {}) {
    this.element = element;
    this.state = element.dataset.state === 'open' ? 'open' : 'closed';
    this.trigger = null;
    this.backdropElement = null;

    const backdropOption = options.backdrop ?? element.getAttribute('data-ui-backdrop');
    this.backdrop =
      backdropOption === 'static' ? 'static' : readBooleanOption(backdropOption, true);
    this.keyboard = readBooleanOption(
      options.keyboard ?? element.getAttribute('data-ui-keyboard'),
      true
    );

    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);

    if (!element.hasAttribute('role')) {
      element.setAttribute('role', 'dialog');
    }
    if (!element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '-1');
    }

    element.dataset.uiModal = '';
    element.dataset.state = this.state;
    element.setAttribute('aria-hidden', this.state === 'open' ? 'false' : 'true');
  }

  isOpen() {
    return this.state === 'opening' || this.state === 'open';
  }

  async show(trigger = null) {
    if (this.isOpen() || this.state === 'closing') {
      return false;
    }

    let returnFocusTarget = trigger || document.activeElement;

    if (activeModal && activeModal !== this) {
      const previousModal = activeModal;
      const returnFocusToPreviousTrigger =
        previousModal.element.contains(returnFocusTarget);
      const previousTrigger = previousModal.trigger;
      const wasHidden = await previousModal.hide({ restoreFocus: false });
      if (!wasHidden) {
        return false;
      }
      if (returnFocusToPreviousTrigger && previousTrigger) {
        returnFocusTarget = previousTrigger;
      }
    }

    if (!dispatch(this.element, MODAL_EVENTS.SHOW, trigger)) {
      return false;
    }

    this.trigger = returnFocusTarget;
    this.state = 'opening';
    activeModal = this;

    this.createBackdrop();
    document.body.classList.add('ui-modal-open');
    disableBodyScroll(this.element);

    this.element.style.display = 'block';
    this.element.setAttribute('aria-hidden', 'false');
    this.element.setAttribute('aria-modal', 'true');
    this.element.offsetHeight;
    this.element.dataset.state = this.state;

    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('focusin', this.handleFocus);
    this.element.addEventListener('click', this.handleModalClick);

    await waitForTransition(this.element);

    if (this.state !== 'opening') {
      return false;
    }

    this.state = 'open';
    this.element.dataset.state = this.state;
    this.focusInitialElement();
    dispatch(this.element, MODAL_EVENTS.SHOWN, trigger);

    return true;
  }

  async hide({ restoreFocus = true } = {}) {
    if (!this.isOpen()) {
      return false;
    }

    if (!dispatch(this.element, MODAL_EVENTS.HIDE, this.trigger)) {
      return false;
    }

    this.state = 'closing';
    this.element.dataset.state = this.state;
    if (this.backdropElement) {
      this.backdropElement.dataset.state = 'closing';
    }

    await waitForTransition(this.element);

    this.element.style.display = '';
    this.element.removeAttribute('aria-modal');
    this.element.setAttribute('aria-hidden', 'true');
    this.removeBackdrop();

    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('focusin', this.handleFocus);
    this.element.removeEventListener('click', this.handleModalClick);

    this.state = 'closed';
    this.element.dataset.state = this.state;
    if (activeModal === this) {
      activeModal = null;
      document.body.classList.remove('ui-modal-open');
      enableBodyScroll(this.element);
    }

    if (restoreFocus) {
      focusElement(this.trigger);
    }

    dispatch(this.element, MODAL_EVENTS.HIDDEN, this.trigger);
    this.trigger = null;

    return true;
  }

  toggle(trigger = null) {
    return this.isOpen() ? this.hide() : this.show(trigger);
  }

  createBackdrop() {
    if (!this.backdrop || this.backdropElement) {
      return;
    }

    const backdrop = document.createElement('div');
    backdrop.className = 'ui-modal-backdrop';
    backdrop.dataset.uiModalBackdrop = '';
    backdrop.dataset.state = 'closed';

    if (this.element.classList.contains('ui-modal--animated')) {
      backdrop.classList.add('ui-modal-backdrop--animated');
    }

    document.body.appendChild(backdrop);
    backdrop.offsetHeight;
    backdrop.dataset.state = 'opening';
    this.backdropElement = backdrop;
  }

  removeBackdrop() {
    if (!this.backdropElement) {
      return;
    }

    this.backdropElement.dataset.state = 'closed';
    this.backdropElement.remove();
    this.backdropElement = null;
  }

  focusInitialElement() {
    const preferred = this.element.querySelector(
      '[data-modal-initial-focus], [autofocus]'
    );
    const [first] = focusableElements(this.element);

    if (!focusElement(preferred) && !focusElement(first)) {
      focusElement(this.element);
    }
  }

  handleKeydown(event) {
    if (activeModal !== this) {
      return;
    }

    if (event.key === 'Escape' && this.keyboard) {
      event.preventDefault();
      this.hide();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusables = focusableElements(this.element);
    if (focusables.length === 0) {
      event.preventDefault();
      focusElement(this.element);
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const focusIsOutside = !this.element.contains(document.activeElement);

    if (event.shiftKey && (document.activeElement === first || focusIsOutside)) {
      event.preventDefault();
      focusElement(last);
    } else if (!event.shiftKey && (document.activeElement === last || focusIsOutside)) {
      event.preventDefault();
      focusElement(first);
    }
  }

  handleFocus(event) {
    if (activeModal === this && !this.element.contains(event.target)) {
      this.focusInitialElement();
    }
  }

  handleModalClick(event) {
    if (event.target === this.element && this.backdrop !== 'static') {
      this.hide();
    }
  }
}

export function getModal(elementOrSelector, options) {
  const element = resolveElement(elementOrSelector);
  if (!element) {
    return null;
  }

  if (!instances.has(element)) {
    instances.set(element, new Modal(element, options));
  }

  return instances.get(element);
}

export function getActiveModal() {
  return activeModal;
}
